// Copyright (c) Ensho Health, 2021 to present

import { gql } from 'graphql-request';
import { privateClient, workflowClient } from './client-gql.js';

export async function fetchPii(patientId) {
  try {
    const query = gql`
      query pii($patientId: ID!) {
        patient(id: $patientId) {
          pii {
            name
            dob
            hcn
            postal
          }
        }
      }
    `;
    const variables = { patientId };
    const response = await workflowClient.request(query, variables);
    // in some cases patient can be null here (if the user is not allowed to
    // see this patient)
    let res = {
      name: '[encrypted]',
      dob: '[encrypted]',
      hcn: '[encrypted]',
      postal: '[encrypted]'
    };
    if (response.patient) {
      res = { ...response.patient.pii };
    }
    return res;
  } catch (error) {
    throw error;
  }
}

export async function fetchPublicClinicKeys(clinicId) {
  try {
    const query = gql`
      query keys($clinicId: Int!) {
        keys {
          userHasAccess
          randomSalt
          keyChangeEnabled(clinic: $clinicId)
          cryptoParameters(version: "latest") {
            id: cryptoparemetersId
            scryptN
            scryptR
            scryptP
            scryptDklen
            RSABits: rsaBits
            RSAExponent: rsaExponent
          }
          site(clinicId: $clinicId) {
            keyId
            keyVersion
            clinicId
            sitePubkey
            siteSalt
            cryptoParameters {
              id: cryptoparemetersId
              scryptN
              scryptR
              scryptP
              scryptDklen
              RSABits: rsaBits
              RSAExponent: rsaExponent
            }
          }
        }
      }
    `;
    const variables = { clinicId };
    const response = await privateClient.request(query, variables);
    return response?.keys;
  } catch (error) {
    throw error;
  }
}

export async function getEncryptedAliases(aliases) {
  try {
    const query = gql`
      query getEncryptedAliases($aliases: [String]) {
        encryptedLinks(aliases: $aliases) {
          alias
          salt
          ciphertext
        }
      }
    `;
    const variables = { aliases };
    const response = await privateClient.request(query, variables);
    const res = {};
    if (response?.encryptedLinks?.length > 0) {
      const nonNullLinks = response.encryptedLinks.filter((link) => !!link);
      for (let linkIndex = 0; linkIndex < nonNullLinks.length; linkIndex++) {
        const link = nonNullLinks[linkIndex];
        res[link.alias] = link;
      }
    }
    return res;
  } catch (error) {
    throw error;
  }
}

export async function userNeedsHelp(helpCode) {
  try {
    const query = gql`
      mutation userNeedsHelp($helpCode: String!) {
        userNeedsHelp(helpCode: $helpCode) {
          ok
          error
        }
      }
    `;
    const variables = { helpCode };
    const response = await workflowClient.request(query, variables);
    return response?.userNeedsHelp;
  } catch (error) {
    throw error;
  }
}

export async function setSitePublicKey(
  clinicId,
  cryptoParametersId,
  sitePublicKey,
  siteSalt
) {
  try {
    const query = gql`
      mutation setSitePublicKey(
        $clinic: Int!
        $cryptoParametersId: Int!
        $sitePublicKey: String!
        $siteSalt: String!
      ) {
        setSitePublicKey(
          clinic: $clinic
          cryptoParametersId: $cryptoParametersId
          sitePublicKey: $sitePublicKey
          siteSalt: $siteSalt
        ) {
          ok
        }
      }
    `;
    const variables = {
      clinic: clinicId,
      cryptoParametersId,
      sitePublicKey,
      siteSalt
    };
    const response = await privateClient.request(query, variables);
    return response?.setSitePublicKey;
  } catch (error) {
    throw error;
  }
}
